import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api"; // Ensure BASE_URL is correctly defined
import { IMG_URL } from "../config/api"; // Ensure BASE_URL is correctly defined
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf"; // Import jsPDF
import "jspdf-autotable"; // Import for table support
import * as XLSX from "xlsx"; // Import xlsx for Excel export
import Swal from "sweetalert2";

const BillModuleList = () => {
  const [billModules, setBillModules] = useState([]);
  const [filteredBillModules, setFilteredBillModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BASE_URL}billmodule`)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 1) {
          // Parsing images field as the response gives a JSON string
          const parsedBillModules = data.data.map((billModule) => ({
            ...billModule,
            images: JSON.parse(billModule.images), // Parse the images field to an array
          }));
          setBillModules(parsedBillModules);
          setFilteredBillModules(parsedBillModules);
        } else {
          alert("Failed to fetch Bill modules");
        }
      })
      .catch((error) => console.error("Error fetching bill modules:", error));
  }, []);

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    const filtered = billModules.filter((billModule) =>
      billModule.party_name.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredBillModules(filtered);
  };

  const handleEdit = (id) => {
    navigate(`/EditBillModule/${id}`);
  };

  const handleAdd = () => {
    navigate("/AddBill");
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This bill module will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${BASE_URL}billmodule/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status === 1) {
              const updated = billModules.filter((bill) => bill.id !== id);
              setBillModules(updated);
              setFilteredBillModules(updated);
              Swal.fire(
                "Deleted!",
                "The bill module has been deleted.",
                "success"
              );
            } else {
              Swal.fire(
                "Failed!",
                "Failed to delete the bill module.",
                "error"
              );
            }
          })
          .catch((error) => {
            console.error("Error deleting bill module:", error);
            Swal.fire("Error!", "Something went wrong.", "error");
          });
      }
    });
  };


  // Export filteredBillModules to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Bill Modules List", 14, 10); // Add a title to the PDF

    // Map data for the table with Sr No.
    const tableData = filteredBillModules.map((billModule, index) => [
      index + 1, // Sr No.
      billModule.party_name || "N/A",
      billModule.images && Array.isArray(billModule.images)
        ? billModule.images.join(", ")
        : "No Images",
    ]);

    // Add table to the PDF
    doc.autoTable({
      head: [["Sr No.", "Party Name", "Images"]], // Table headers
      body: tableData,
    });

    // Save the PDF
    doc.save("bill-modules-list.pdf");
  };

  // Export filteredBillModules to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredBillModules.map((billModule, index) => ({
        SrNo: index + 1, // Sr No.
        PartyName: billModule.party_name || "N/A",
        Images:
          billModule.images && Array.isArray(billModule.images)
            ? billModule.images.join(", ")
            : "No Images",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bill Modules List");

    // Save the Excel file with a filename
    XLSX.writeFile(workbook, "bill-modules-list.xlsx");
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Bill Module List</h3>
            </div>
            <div className="col-12 col-md-6 order-md-2 order-first">
              <nav
                aria-label="breadcrumb"
                className="breadcrumb-header float-start float-lg-end"
              >
                <ol className="breadcrumb"></ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Party Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search bill module party name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-primary me-2"
                      onClick={handleAdd}
                    >
                      Add Bill Module
                    </button>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    {/* <button className="btn btn-secondary" onClick={handleExportExcel}>Export to Excel</button> */}
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Sr No.</th>
                        <th>Party Name</th>
                        <th>Images</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBillModules.map((billModule, index) => (
                        <tr key={billModule.id}>
                          <td>{index + 1}</td> {/* Display Sr No. */}
                          <td>{billModule.party_name || "N/A"}</td>
                          <td>
                            {billModule.images &&
                            Array.isArray(billModule.images) &&
                            billModule.images.length > 0 ? (
                              <div className="row">
                                {billModule.images.map((image, idx) => (
                                  <div key={idx} className="col-2">
                                    {/* Ensure image path is correctly concatenated */}
                                    <img
                                      src={
                                        image.startsWith("http")
                                          ? image
                                          : `${IMG_URL}${image}`
                                      }
                                      alt={`Bill Module ${idx + 1}`}
                                      style={{ width: "100px", height: "auto" }}
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              "No Images"
                            )}
                          </td>
                          <td className="text-end">
                            {/* <button className="btn btn-success" onClick={() => handleEdit(billModule.id)}>Edit</button> */}
                            <button
                              className="btn btn-danger ms-2"
                              onClick={() => handleDelete(billModule.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BillModuleList;
